import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SupportTicket, TicketMessage } from '@/types';
import { useToast } from './use-toast';
import { Database } from '@/integrations/supabase/types';

type TicketCategory = Database['public']['Enums']['ticket_category'];
type TicketPriority = Database['public']['Enums']['ticket_priority'];
type TicketStatus = Database['public']['Enums']['ticket_status'];

const formatTicketId = (id: string) => {
  // Extract a numeric sequence from the UUID and format it nicely
  const numericPart = parseInt(id.replace(/[^0-9]/g, '').slice(0, 6)) || Math.floor(Math.random() * 999999);
  return `ATP-${numericPart.toString().padStart(6, '0')}`;
};

export const useSupportTickets = (authorId?: string) => {
  const { toast } = useToast();

  return useQuery({
    queryKey: ['supportTickets', authorId],
    queryFn: async (): Promise<SupportTicket[]> => {
      let query = supabase
        .from('support_tickets')
        .select(`
          *,
          profiles:author_id (
            name,
            email
          )
        `)
        .order('updated_at', { ascending: false });
      
      if (authorId) {
        query = query.eq('author_id', authorId);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error loading support tickets:', error);
        toast({
          title: 'Error loading support tickets',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
      
      const tickets = data as any[];
      
      // Fetch messages for each ticket
      const ticketsWithMessages = await Promise.all(
        tickets.map(async (ticket) => {
          const { data: messages, error: messagesError } = await supabase
            .from('ticket_messages')
            .select(`
              *,
              profiles:user_id (
                name,
                role
              )
            `)
            .eq('ticket_id', ticket.id)
            .order('created_at', { ascending: true });
          
          if (messagesError) {
            console.error('Error fetching messages for ticket:', ticket.id, messagesError);
            return {
              id: ticket.id,
              displayId: formatTicketId(ticket.id),
              authorId: ticket.author_id,
              subject: ticket.subject,
              category: ticket.category,
              priority: ticket.priority,
              status: ticket.status,
              createdAt: ticket.created_at,
              updatedAt: ticket.updated_at,
              messages: []
            };
          }
          
          return {
            id: ticket.id,
            displayId: formatTicketId(ticket.id),
            authorId: ticket.author_id,
            subject: ticket.subject,
            category: ticket.category,
            priority: ticket.priority,
            status: ticket.status,
            createdAt: ticket.created_at,
            updatedAt: ticket.updated_at,
            messages: messages.map((msg: any) => ({
              id: msg.id,
              ticketId: msg.ticket_id,
              userId: msg.user_id,
              userRole: msg.profiles?.role,
              userName: msg.profiles?.name,
              message: msg.message,
              attachments: msg.attachments,
              createdAt: msg.created_at
            }))
          };
        })
      );
      
      return ticketsWithMessages as SupportTicket[];
    }
  });
};

export const useSupportTicket = (ticketId: string) => {
  const { toast } = useToast();

  return useQuery({
    queryKey: ['supportTicket', ticketId],
    queryFn: async (): Promise<SupportTicket> => {
      console.log('Fetching ticket details for ID:', ticketId);
      
      // Fetch ticket details
      const { data: ticket, error: ticketError } = await supabase
        .from('support_tickets')
        .select(`
          *,
          profiles:author_id (
            name,
            email
          )
        `)
        .eq('id', ticketId)
        .single();
      
      if (ticketError) {
        console.error('Error loading ticket:', ticketError);
        toast({
          title: 'Error loading ticket',
          description: ticketError.message,
          variant: 'destructive',
        });
        throw ticketError;
      }
      
      // Fetch messages for the ticket
      const { data: messages, error: messagesError } = await supabase
        .from('ticket_messages')
        .select(`
          *,
          profiles:user_id (
            name,
            role
          )
        `)
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });
      
      if (messagesError) {
        console.error('Error loading messages:', messagesError);
        toast({
          title: 'Error loading messages',
          description: messagesError.message,
          variant: 'destructive',
        });
        throw messagesError;
      }
      
      console.log('Ticket loaded successfully:', ticket);
      console.log('Messages loaded:', messages);
      
      // Transform data to match our model
      const transformedTicket: SupportTicket = {
        id: ticket.id,
        displayId: formatTicketId(ticket.id),
        authorId: ticket.author_id,
        subject: ticket.subject,
        category: ticket.category,
        priority: ticket.priority,
        status: ticket.status,
        createdAt: ticket.created_at,
        updatedAt: ticket.updated_at,
        messages: messages.map((msg: any) => ({
          id: msg.id,
          ticketId: msg.ticket_id,
          userId: msg.user_id,
          userRole: msg.profiles?.role,
          userName: msg.profiles?.name,
          message: msg.message,
          attachments: msg.attachments,
          createdAt: msg.created_at
        }))
      };
      
      return transformedTicket;
    },
    enabled: !!ticketId
  });
};

export const useCreateSupportTicket = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      authorId,
      subject,
      category,
      priority,
      message
    }: {
      authorId: string;
      subject: string;
      category: TicketCategory;
      priority: TicketPriority;
      message: string;
    }) => {
      console.log('Creating support ticket with data:', { authorId, subject, category, priority, message });
      
      // First create the ticket
      const ticketData = {
        author_id: authorId,
        subject,
        category,
        priority,
        status: 'open' as TicketStatus
      };
      
      const { data: ticket, error: ticketError } = await supabase
        .from('support_tickets')
        .insert(ticketData)
        .select()
        .single();
      
      if (ticketError) {
        console.error('Error creating support ticket:', ticketError);
        toast({
          title: 'Error creating support ticket',
          description: ticketError.message,
          variant: 'destructive',
        });
        throw ticketError;
      }
      
      console.log('Ticket created successfully:', ticket);
      
      // Then add the first message
      const messagePayload = {
        ticket_id: ticket.id,
        user_id: authorId,
        message
      };
      
      const { data: messageResponse, error: messageError } = await supabase
        .from('ticket_messages')
        .insert(messagePayload)
        .select()
        .single();
      
      if (messageError) {
        console.error('Error creating message:', messageError);
        toast({
          title: 'Error creating message',
          description: messageError.message,
          variant: 'destructive',
        });
        throw messageError;
      }
      
      console.log('Message created successfully:', messageResponse);
      
      return { ticket, message: messageResponse };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supportTickets'] });
      toast({
        title: 'Success',
        description: 'Support ticket has been created successfully.',
      });
    }
  });
};

export const useAddTicketMessage = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      ticketId,
      userId,
      message
    }: {
      ticketId: string;
      userId: string;
      message: string;
    }) => {
      console.log('Adding message to ticket:', { ticketId, userId, message });
      
      // Add the message
      const messageData = {
        ticket_id: ticketId,
        user_id: userId,
        message
      };
      
      const { data, error } = await supabase
        .from('ticket_messages')
        .insert(messageData)
        .select()
        .single();
      
      if (error) {
        console.error('Error sending message:', error);
        toast({
          title: 'Error sending message',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
      
      console.log('Message added successfully:', data);
      
      // Update the ticket's updated_at timestamp
      const { error: updateError } = await supabase
        .from('support_tickets')
        .update({
          updated_at: new Date().toISOString()
        })
        .eq('id', ticketId);
      
      if (updateError) {
        console.error('Error updating ticket timestamp:', updateError);
      }
      
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['supportTicket', variables.ticketId] });
      queryClient.invalidateQueries({ queryKey: ['supportTickets'] });
      toast({
        title: 'Success',
        description: 'Message sent successfully.',
      });
    }
  });
};

export const useUpdateTicketStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      ticketId,
      status
    }: {
      ticketId: string;
      status: TicketStatus;
    }) => {
      console.log('Updating ticket status:', { ticketId, status });
      
      const updateData = {
        status,
        updated_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('support_tickets')
        .update(updateData)
        .eq('id', ticketId)
        .select()
        .single();
      
      if (error) {
        console.error('Error updating ticket status:', error);
        toast({
          title: 'Error updating ticket status',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
      
      console.log('Ticket status updated successfully:', data);
      
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['supportTicket', variables.ticketId] });
      queryClient.invalidateQueries({ queryKey: ['supportTickets'] });
      toast({
        title: 'Success',
        description: 'Ticket status has been updated.',
      });
    }
  });
};

export const useDeleteSupportTicket = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (ticketId: string) => {
      console.log('Deleting support ticket:', ticketId);
      
      // First, delete all messages associated with the ticket
      const { error: messagesError } = await supabase
        .from('ticket_messages')
        .delete()
        .eq('ticket_id', ticketId);
      
      if (messagesError) {
        console.error('Error deleting ticket messages:', messagesError);
        toast({
          title: 'Error deleting ticket messages',
          description: messagesError.message,
          variant: 'destructive',
        });
        throw messagesError;
      }
      
      console.log('Ticket messages deleted successfully');
      
      // Then delete the ticket itself
      const { error: ticketError } = await supabase
        .from('support_tickets')
        .delete()
        .eq('id', ticketId);
      
      if (ticketError) {
        console.error('Error deleting support ticket:', ticketError);
        toast({
          title: 'Error deleting support ticket',
          description: ticketError.message,
          variant: 'destructive',
        });
        throw ticketError;
      }
      
      console.log('Support ticket deleted successfully');
      
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supportTickets'] });
      toast({
        title: 'Success',
        description: 'Support ticket has been deleted successfully.',
      });
    }
  });
};
